import * as React from "react";
import { NavLink, Outlet, useLocation, useMatches, Link, useNavigate } from "react-router";
import { ChevronDown, LogOut, Menu as MenuIcon, X, Building2, ShieldAlert, Moon, Sun, Bell, PanelLeftClose, PanelLeftOpen, UserRound } from "lucide-react";
import { Badge, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, Tooltip, cn } from "@cm/ui";
import { useSession } from "../lib/session";
import type { NavGroup, NavItem } from "./nav";
import { BrandLogo } from "../components/BrandLogo";
import { trackAction, useUsageTracking } from "../lib/usage";
import { useBrand } from "../lib/brand";
import { useWsQuery } from "../lib/hooks";
import { AssistantPanel } from "../pages/app/guide/AssistantPanel";
import { GuideOverlay } from "../pages/app/guide/GuideOverlay";

const ITEM_BASE = "relative flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-sidebar-indicator";
const ITEM_ACTIVE = "bg-sidebar-active font-medium text-sidebar-active-fg before:absolute before:inset-y-1.5 before:-left-2 before:w-1 before:rounded-r before:bg-sidebar-indicator";
const ITEM_IDLE = "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg";

/** Menu recolhido (só ícones) no desktop; preferência do navegador (localStorage). */
function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem("cm.sidebar") === "collapsed";
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem("cm.sidebar", c ? "expanded" : "collapsed");
      } catch {
        /* ignora (modo privado) */
      }
      return !c;
    });
  return [collapsed, toggle];
}

function Sidebar({ groups, onNavigate, collapsed = false }: { groups: NavGroup[]; onNavigate?: () => void; collapsed?: boolean }) {
  const { can, isPlatform } = useSession();
  const loc = useLocation();
  const visible = groups
    .map((g) => ({ ...g, items: g.items.filter((it) => isPlatform || (it.anyPermission ? it.anyPermission.some((perm) => can(perm)) : !it.permission || can(it.permission))) }))
    .filter((g) => g.items.length);
  const under = (path: string, base: string) => path === base || path.startsWith(`${base}/`);
  const matchList = (it: NavItem) => (it.match ? (Array.isArray(it.match) ? it.match : [it.match]) : []);
  const itemActive = (it: NavItem) => (it.end ? loc.pathname === it.to : under(loc.pathname, it.to)) || matchList(it).some((m) => under(loc.pathname, m));
  // Accordion: um módulo aberto por vez. O módulo da rota atual abre ao navegar.
  const activeGroupId = visible.find((g) => g.id && g.items.some(itemActive))?.id ?? null;
  const [openId, setOpenId] = React.useState<string | null>(activeGroupId);
  React.useEffect(() => {
    if (activeGroupId) setOpenId(activeGroupId);
  }, [activeGroupId]);
  const toggle = (id: string) => setOpenId((cur) => (cur === id ? null : id));
  const renderItems = (items: NavItem[], nested: boolean) => (
    <ul className={cn("flex flex-col gap-0.5", nested && !collapsed && "ml-4 border-l border-sidebar-border pl-2")}>
      {items.map((it) => {
        const link = (
          <NavLink
            to={it.to}
            end={it.end}
            onClick={onNavigate}
            aria-label={collapsed ? it.label : undefined}
            className={({ isActive }) => cn(ITEM_BASE, collapsed && "justify-center px-0 py-2", isActive || (it.match && itemActive(it)) ? ITEM_ACTIVE : ITEM_IDLE)}
          >
            <it.icon className={cn("shrink-0", collapsed ? "h-[18px] w-[18px]" : "h-4 w-4")} aria-hidden />
            {!collapsed && <span className="truncate">{it.label}</span>}
          </NavLink>
        );
        return (
          <li key={it.to}>
            {collapsed ? (
              // Wrapper: o Slot do Tooltip não combina com className em função do NavLink.
              <Tooltip content={it.label} side="right">
                <span className="block">{link}</span>
              </Tooltip>
            ) : (
              link
            )}
          </li>
        );
      })}
    </ul>
  );
  // Recolhido: só ícones; títulos de seção viram uma linha e submenus ficam abertos (sem rótulo).
  if (collapsed) {
    return (
      <nav aria-label="Navegação principal" className="flex flex-col gap-1 px-2 py-4">
        {visible.map((g, i) => (
          <div key={g.id ?? `flat-${i}`} className={cn(i > 0 && "mt-2 border-t border-sidebar-border pt-2")}>
            {renderItems(g.items, false)}
          </div>
        ))}
      </nav>
    );
  }
  return (
    <nav aria-label="Navegação principal" className="flex flex-col gap-1 px-3 py-4">
      {visible.map((g, i) => {
        // Grupo sem id fica achatado (sem submenu); com rótulo vira seção fixa com título; `divider` separa por linha.
        if (!g.id)
          return (
            <div key={`flat-${i}`} className={cn(i > 0 && "mt-3", g.divider && "border-t border-sidebar-border pt-3")}>
              {g.label && <p className="mb-1 px-2.5 text-[11px] font-semibold uppercase tracking-wider text-sidebar-muted">{g.label}</p>}
              {renderItems(g.items, false)}
            </div>
          );
        const Icon = g.icon;
        const isActiveGroup = g.items.some(itemActive);
        const expanded = openId === g.id;
        return (
          <div key={g.id}>
            <button
              type="button"
              onClick={() => toggle(g.id!)}
              aria-expanded={expanded}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-sidebar-indicator",
                isActiveGroup ? "font-medium text-sidebar-fg" : "text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-fg",
              )}
            >
              {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden />}
              <span className="flex-1 truncate text-left">{g.label}</span>
              <ChevronDown className={cn("h-4 w-4 shrink-0 text-sidebar-muted transition-transform", expanded && "rotate-180")} aria-hidden />
            </button>
            {expanded && <div className="mt-0.5">{renderItems(g.items, true)}</div>}
          </div>
        );
      })}
    </nav>
  );
}

/** Alternador de tema (claro/escuro) — persiste em localStorage e aplica data-theme no <html>. */
function useTheme(): ["claro" | "escuro", () => void] {
  const [theme, setTheme] = React.useState<"claro" | "escuro">(() => {
    try {
      return localStorage.getItem("cm.theme") === "escuro" ? "escuro" : "claro";
    } catch {
      return "claro";
    }
  });
  React.useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("cm.theme", theme);
    } catch {
      /* ignora (modo privado) */
    }
  }, [theme]);
  return [theme, () => setTheme((t) => (t === "claro" ? "escuro" : "claro"))];
}

/** Sino da central de notificações impeditivas (contador de problemas ativos do workspace). */
function NotificationBell() {
  const q = useWsQuery<{ active: number; blocking: number }>("/api/app/alerts/summary", { refetchInterval: 60_000, retry: false });
  const n = q.data?.active ?? 0;
  const blocking = (q.data?.blocking ?? 0) > 0;
  const label = n ? `Notificações: ${n} problema${n === 1 ? "" : "s"} ${blocking ? "impeditivo" + (n === 1 ? "" : "s") : "ativo" + (n === 1 ? "" : "s")}` : "Notificações";
  return (
    <Link to="/app/notifications" className="relative rounded p-1.5 text-muted hover:bg-surface-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-focus" aria-label={label} title={label}>
      <Bell className="h-5 w-5" aria-hidden />
      {n > 0 && (
        <span className={cn("absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-semibold leading-none text-white", blocking ? "bg-danger" : "bg-warning")} aria-hidden>
          {n > 9 ? "9+" : n}
        </span>
      )}
    </Link>
  );
}

function ThemeToggle() {
  const [theme, toggle] = useTheme();
  const dark = theme === "escuro";
  return (
    <button
      type="button"
      onClick={() => {
        toggle();
        trackAction("theme.toggle");
      }}
      className="grid h-8 w-8 place-items-center rounded-md text-muted hover:bg-surface-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-focus"
      aria-label={dark ? "Mudar para tema claro" : "Mudar para tema escuro"}
      title={dark ? "Tema claro" : "Tema escuro"}
    >
      {dark ? <Sun className="h-[18px] w-[18px]" aria-hidden /> : <Moon className="h-[18px] w-[18px]" aria-hidden />}
    </button>
  );
}

function WorkspaceSwitcher() {
  const { me, activeWorkspace, switchWorkspace } = useSession();
  if (!me || me.kind !== "user") return null;
  const byOrg = new Map<string, typeof me.workspaces>();
  for (const w of me.workspaces) byOrg.set(w.organization_name, [...(byOrg.get(w.organization_name) ?? []), w]);
  return (
    <Menu>
      <MenuTrigger className="flex min-w-0 max-w-[16rem] items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-left text-sm hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-focus" aria-label="Trocar workspace">
        <Building2 className="h-4 w-4 shrink-0 text-muted" aria-hidden />
        <span className="min-w-0">
          <span className="block truncate text-[11px] leading-tight text-subtle">{activeWorkspace?.organization_name ?? "Sem organização"}</span>
          <span className="block truncate font-medium leading-tight">{activeWorkspace?.name ?? "Selecione"}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted" aria-hidden />
      </MenuTrigger>
      <MenuContent align="start">
        {[...byOrg.entries()].map(([org, list]) => (
          <React.Fragment key={org}>
            <MenuLabel>{org}</MenuLabel>
            {list.map((w) => (
              <MenuItem key={w.id} onSelect={() => {
                  trackAction("workspace.switch");
                  void switchWorkspace(w.id);
                }}>
                <span className={cn("truncate", w.id === activeWorkspace?.id && "font-semibold")}>{w.name}</span>
              </MenuItem>
            ))}
          </React.Fragment>
        ))}
      </MenuContent>
    </Menu>
  );
}

export function Shell({ groups, panel }: { groups: NavGroup[]; panel: "app" | "admin" | "master" }) {
  const { me, logout } = useSession();
  const navigate = useNavigate();
  const brand = useBrand();
  const sidebarTone = brand.theme.sidebar === "light" ? "dark" : "light";
  const [open, setOpen] = React.useState(false);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const loc = useLocation();
  // Rotas com handle.layout = "fill" usam largura total e altura da viewport (ex.: kanban).
  const fill = useMatches().some((m) => (m.handle as { layout?: string } | undefined)?.layout === "fill");
  React.useEffect(() => setOpen(false), [loc.pathname]);
  const panelLabel = panel === "master" ? "Master" : panel === "admin" ? "Admin da marca" : null;
  // Métricas de uso (Master): só painel do cliente e Admin da marca; sessões de suporte são ignoradas no servidor.
  useUsageTracking(panel === "master" ? null : panel);
  return (
    <div className="flex h-full">
      <a href="#conteudo" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2">
        Pular para o conteúdo
      </a>
      <aside className={cn("hidden shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200 lg:flex", collapsed ? "w-16" : "w-60")}>
        <div className={cn("flex h-14 items-center gap-2 border-b border-sidebar-border", collapsed ? "justify-center px-2" : "px-4")}>
          {collapsed ? <BrandLogo variant="mark" tone={sidebarTone} className="h-7 w-7" /> : <BrandLogo tone={sidebarTone} className="h-6 max-w-[11rem]" />}
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <Sidebar groups={groups} collapsed={collapsed} />
        </div>
        <div className={cn("border-t border-sidebar-border p-2", collapsed && "flex justify-center")}>
          <Tooltip content={collapsed ? "Expandir menu" : "Recolher menu"} side="right">
            <button
              type="button"
              onClick={toggleCollapsed}
              aria-label={collapsed ? "Expandir menu" : "Recolher menu"}
              aria-expanded={!collapsed}
              className={cn("flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-sidebar-muted transition-colors hover:bg-sidebar-hover hover:text-sidebar-fg focus-visible:outline-2 focus-visible:outline-sidebar-indicator", !collapsed && "w-full")}
            >
              {collapsed ? <PanelLeftOpen className="h-[18px] w-[18px]" aria-hidden /> : <PanelLeftClose className="h-4 w-4" aria-hidden />}
              {!collapsed && <span>Recolher menu</span>}
            </button>
          </Tooltip>
        </div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 overflow-y-auto bg-sidebar shadow-lg">
            <div className="flex h-14 items-center justify-between border-b border-sidebar-border px-4">
              <BrandLogo tone={sidebarTone} className="h-6 max-w-[11rem]" />
              <button onClick={() => setOpen(false)} aria-label="Fechar menu" className="rounded p-1 text-sidebar-fg hover:bg-sidebar-hover">
                <X className="h-5 w-5" />
              </button>
            </div>
            <Sidebar groups={groups} onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="relative flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-4">
          <button className="rounded p-1.5 hover:bg-surface-muted lg:hidden" onClick={() => setOpen(true)} aria-label="Abrir menu">
            <MenuIcon className="h-5 w-5" />
          </button>
          {panel === "app" && <WorkspaceSwitcher />}
          {panelLabel && (
            <Badge tone={panel === "master" ? "danger" : "brand"} className="uppercase tracking-wide">
              {panelLabel}
            </Badge>
          )}
          {me?.kind === "support" && (
            <Badge tone="warning">
              <ShieldAlert className="h-3 w-3" aria-hidden /> Sessão de suporte (somente leitura)
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            {panel === "app" && me?.kind !== "support" && <AssistantPanel />}
            {panel === "app" && <NotificationBell />}
            <ThemeToggle />
            {panel === "app" && me?.is_brand_admin && (
              <Link to="/admin/overview" className="hidden text-sm text-muted hover:text-fg sm:inline">
                Admin da marca
              </Link>
            )}
            {panel === "admin" && (
              <Link to="/app/overview" className="hidden text-sm text-muted hover:text-fg sm:inline">
                Painel do usuário
              </Link>
            )}
            <Menu>
              <MenuTrigger className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-surface-muted focus-visible:outline-2 focus-visible:outline-focus" aria-label="Menu do usuário">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-surface-muted text-xs font-semibold" aria-hidden>
                  {me?.user.name?.slice(0, 1) ?? "?"}
                </span>
                <span className="hidden max-w-[10rem] truncate md:inline">{me?.user.name}</span>
              </MenuTrigger>
              <MenuContent>
                <MenuLabel>{me?.user.email}</MenuLabel>
                <MenuSeparator />
                {/* Troca de painel também no menu (no celular os links inline ficam ocultos). */}
                {panel === "app" && me?.is_brand_admin && (
                  <MenuItem className="sm:hidden" onSelect={() => navigate("/admin/overview")}>
                    Admin da marca
                  </MenuItem>
                )}
                {panel === "admin" && (
                  <MenuItem className="sm:hidden" onSelect={() => navigate("/app/overview")}>
                    Painel do usuário
                  </MenuItem>
                )}
                {(panel === "app" || panel === "admin") && me?.kind === "user" && (
                  <MenuItem onSelect={() => navigate(`/${panel}/profile`)}>
                    <UserRound className="h-4 w-4" aria-hidden /> Meu perfil
                  </MenuItem>
                )}
                <MenuItem onSelect={() => void logout()}>
                  <LogOut className="h-4 w-4" aria-hidden /> Sair
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        </header>
        <main id="conteudo" className={cn("relative min-w-0 flex-1", fill ? "flex min-h-0 flex-col overflow-y-auto lg:overflow-hidden" : "overflow-y-auto")}>
          <div className={fill ? "flex min-h-0 w-full flex-1 flex-col px-4 pb-3 pt-5 sm:px-6" : "mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6"}>
            <React.Suspense fallback={<div className="text-sm text-muted">Carregando…</div>}>
              <Outlet />
            </React.Suspense>
          </div>
        </main>
        {panel === "app" && me?.kind !== "support" && <GuideOverlay />}
      </div>
    </div>
  );
}
